import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Radio, Save, Info, Copy } from "lucide-react";
import { toEmbedUrl } from "@/lib/video";
import VideoPlayer from "@/components/site/VideoPlayer";

export default function AdminLive() {
  const [live, setLive] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/live").then((r) => setLive(r.data)).catch(() => {});
  }, []);

  if (!live) return <div className="text-slate-500">Chargement…</div>;

  const update = (k, v) => setLive((l) => ({ ...l, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/live", {
        is_live: live.is_live,
        platform: live.platform,
        stream_url: live.stream_url || "",
        title: live.title || "",
        next_service_date: live.next_service_date || null,
        next_service_title: live.next_service_title || null,
        next_service_description: live.next_service_description || null,
      });
      setLive(data);
      toast.success("Direct mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const embed = toEmbedUrl(live.platform, live.stream_url);

  return (
    <div data-testid="admin-live">
      <div className="mb-8">
        <div className="text-gold-dark text-xs tracking-[0.3em] uppercase">Diffusion</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-deepblue-900 mt-1">Gestion du direct</h1>
        <p className="text-slate-500 mt-2">Activez la diffusion et configurez le prochain culte.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-6 bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex items-center justify-between p-4 rounded-xl bg-deepblue-900 text-white">
            <div className="flex items-center gap-3">
              <Radio className={`w-5 h-5 ${live.is_live ? "text-red-400 live-dot" : "text-white/60"}`} />
              <div>
                <div className="font-serif text-lg">{live.is_live ? "EN DIRECT" : "Hors ligne"}</div>
                <div className="text-xs text-white/60">Bascule visible sur le site public</div>
              </div>
            </div>
            <Switch
              checked={live.is_live}
              onCheckedChange={(v) => update("is_live", v)}
              data-testid="switch-is-live"
            />
          </div>

          <div>
            <Label>Plateforme</Label>
            <Select value={live.platform} onValueChange={(v) => update("platform", v)}>
              <SelectTrigger data-testid="live-platform" className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="youtube">YouTube Live</SelectItem>
                <SelectItem value="facebook">Facebook Live</SelectItem>
                <SelectItem value="vmix">vMix (HLS)</SelectItem>
                <SelectItem value="url">URL directe (HLS / mp4)</SelectItem>
                <SelectItem value="none">Aucune</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>URL du flux</Label>
            <Input
              value={live.stream_url || ""}
              onChange={(e) => update("stream_url", e.target.value)}
              placeholder={live.platform === "vmix"
                ? "https://votre-serveur/hls/eglise.m3u8"
                : "https://www.youtube.com/watch?v=…"}
              className="mt-2"
              data-testid="live-stream-url"
            />
            <p className="text-xs text-slate-400 mt-1">
              {live.platform === "vmix"
                ? "URL HLS (.m3u8) reçue depuis votre serveur RTMP après push vMix."
                : "Accepte URL YouTube, Facebook, HLS (.m3u8) ou fichier .mp4."}
            </p>
          </div>

          {live.platform === "vmix" && <VmixHelpPanel />}

          <div>
            <Label>Titre du direct</Label>
            <Input value={live.title || ""} onChange={(e) => update("title", e.target.value)}
              placeholder="Culte du dimanche" className="mt-2" data-testid="live-title" />
          </div>

          <div className="pt-4 border-t">
            <div className="text-sm font-semibold text-deepblue-900 mb-3">Prochain culte</div>
            <div className="space-y-4">
              <div>
                <Label>Titre</Label>
                <Input value={live.next_service_title || ""} onChange={(e) => update("next_service_title", e.target.value)}
                  className="mt-2" data-testid="next-service-title" />
              </div>
              <div>
                <Label>Date & heure</Label>
                <Input type="datetime-local"
                  value={(live.next_service_date || "").slice(0, 16)}
                  onChange={(e) => update("next_service_date", e.target.value)}
                  className="mt-2" data-testid="next-service-date" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={live.next_service_description || ""} onChange={(e) => update("next_service_description", e.target.value)}
                  rows={3} className="mt-2" data-testid="next-service-description" />
              </div>
            </div>
          </div>

          <Button onClick={save} disabled={saving}
            className="w-full bg-gold hover:bg-gold-hover text-white rounded-full" data-testid="save-live-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>

        <div>
          <div className="text-sm font-semibold text-deepblue-900 mb-3">Aperçu</div>
          <div className="rounded-2xl overflow-hidden bg-black shadow-lg">
            <div className="video-16-9">
              {live.stream_url && embed ? (
                <VideoPlayer type={live.platform} url={embed} muted title="Aperçu" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900 text-white/60 text-sm">
                  Aucune URL de flux configurée
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function VmixHelpPanel() {
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié !");
  };

  return (
    <div className="mt-2 rounded-xl border border-blue-200 bg-blue-50 p-5" data-testid="vmix-help">
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-deepblue-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-deepblue-900">Comment diffuser avec vMix ?</div>
          <p className="mt-2 text-sm text-slate-700">
            vMix pousse un flux <strong>RTMP</strong>. Le site web joue ensuite un flux <strong>HLS (.m3u8)</strong>.
            Deux options selon votre installation :
          </p>

          <div className="mt-4 space-y-4 text-sm">
            <div className="rounded-lg bg-white p-4 border border-blue-200">
              <div className="font-semibold text-deepblue-900 mb-1">Option 1 — Utiliser YouTube Live comme relais (recommandé, gratuit)</div>
              <ol className="list-decimal ml-5 space-y-1 text-slate-700">
                <li>Dans <em>vMix → Stream Settings</em>, choisissez <strong>YouTube</strong>.</li>
                <li>Collez la clé de flux YouTube fournie par YouTube Studio.</li>
                <li>Une fois en direct, changez la plateforme ci-dessus en <strong>YouTube Live</strong> et collez l'URL de la vidéo YouTube en direct.</li>
              </ol>
            </div>

            <div className="rounded-lg bg-white p-4 border border-blue-200">
              <div className="font-semibold text-deepblue-900 mb-1">Option 2 — Serveur RTMP dédié → HLS</div>
              <p className="text-slate-700">
                Utilisez un service qui accepte RTMP en entrée et fournit une URL HLS de lecture :
                <a href="https://www.cloudflare.com/products/cloudflare-stream/" target="_blank" rel="noreferrer"
                   className="text-deepblue-700 underline ml-1">Cloudflare Stream</a>,
                <a href="https://mux.com" target="_blank" rel="noreferrer"
                   className="text-deepblue-700 underline ml-1">Mux</a>,
                <a href="https://restream.io" target="_blank" rel="noreferrer"
                   className="text-deepblue-700 underline ml-1">Restream</a>, ou un serveur nginx-rtmp auto-hébergé.
              </p>
              <div className="mt-3 space-y-2">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Dans vMix — URL RTMP (exemple) :</div>
                  <div className="flex items-center gap-2 bg-slate-100 rounded px-3 py-2 text-xs font-mono">
                    <code className="flex-1 truncate">rtmp://votre-serveur.com/live</code>
                    <button onClick={() => copy("rtmp://votre-serveur.com/live")}><Copy className="w-3.5 h-3.5 text-slate-500" /></button>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Clé de flux vMix (exemple) :</div>
                  <div className="flex items-center gap-2 bg-slate-100 rounded px-3 py-2 text-xs font-mono">
                    <code className="flex-1 truncate">meigda-caanan</code>
                    <button onClick={() => copy("meigda-caanan")}><Copy className="w-3.5 h-3.5 text-slate-500" /></button>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">URL HLS de lecture à coller ci-dessus :</div>
                  <div className="flex items-center gap-2 bg-slate-100 rounded px-3 py-2 text-xs font-mono">
                    <code className="flex-1 truncate">https://votre-serveur.com/hls/meigda-caanan.m3u8</code>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-white p-4 border border-blue-200">
              <div className="font-semibold text-deepblue-900 mb-1">Réglages vMix conseillés</div>
              <ul className="list-disc ml-5 space-y-1 text-slate-700">
                <li>Résolution : <strong>1920×1080</strong> ou <strong>1280×720</strong></li>
                <li>Codec : <strong>H.264</strong> · Débit : <strong>4000-6000 kbps</strong></li>
                <li>Images clés : <strong>toutes les 2 secondes</strong></li>
                <li>Audio : <strong>AAC 128 kbps</strong></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
